import { Switch } from 'antd';
import { ChangeEvent } from 'react';

type Permission = {
  create: boolean | null;
  read: boolean | null;
  update: boolean | null;
  delete: boolean | null;
};

type ModulePermission = {
  moduleId: string;
  permissions: Permission;
};

type UserPermissions = {
  name: string;
  description: string;
  modulePermissions: ModulePermission[];
  colorCode: string;
};

interface Module {
  name: string;
  description: string;
  key: string;
  id: string;
}

interface ModulesData {
  data: Module[];
}

const ModuleManagement = ({
  modules,
  payload,
  setPayload,
}: {
  modules: ModulesData;
  payload: UserPermissions;
  setPayload: React.Dispatch<React.SetStateAction<UserPermissions>>;
}) => {
  const handleToggle = (moduleId: string, checked: boolean) => {
    setPayload((prevState) => {
      const moduleExists = prevState?.modulePermissions?.some(
        (module) => module.moduleId === moduleId
      );

      return {
        ...prevState,
        modulePermissions: moduleExists
          ? prevState.modulePermissions.filter(
              (module) => module.moduleId !== moduleId
            )
          : [
              ...prevState.modulePermissions,
              {
                moduleId,
                permissions: {
                  read: true,
                  create: true,
                  update: true,
                  delete: true,
                },
              },
            ],
      };
    });
  };

  const handleCheckboxChange = (
    moduleId: string,
    permission: keyof Permission,
    checked: boolean
  ) => {
    setPayload((prevState) => ({
      ...prevState,
      modulePermissions: prevState.modulePermissions.map((module) =>
        module.moduleId === moduleId
          ? {
              ...module,
              permissions: {
                ...module.permissions,
                [permission]: checked,
              },
            }
          : module
      ),
    }));
  };

  return (
    <div>
      {modules?.data &&
        modules.data.map((item: Module) => {
          const modulePermission = payload?.modulePermissions?.find(
            (module) => module.moduleId === item.id
          );
          const isEnabled = !!modulePermission;

          return (
            <div key={item.key}>
              <div className="flex justify-between items-centers p-[15px] border-b-[1px] border-b-[#EEEEEE]">
                <h3>{item.name}</h3>
                <Switch
                  checked={isEnabled}
                  onChange={(checked: boolean) =>
                    handleToggle(item.id, checked)
                  }
                />
              </div>
              {isEnabled && (
                <div className="flex gap-[15px] justify-end mr-[15px] py-[20px]">
                  {['read', 'create', 'update', 'delete'].map((permission) => (
                    <span className="flex gap-[10px]" key={permission}>
                      <span>
                        Can{' '}
                        {permission.charAt(0).toUpperCase() +
                          permission.slice(1)}
                      </span>
                      <input
                        type="checkbox"
                        checked={
                          modulePermission?.permissions?.[
                            permission as keyof Permission
                          ] || false
                        }
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleCheckboxChange(
                            item.id,
                            permission as keyof Permission,
                            e.target.checked
                          )
                        }
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};

export default ModuleManagement;
